import { useState, useEffect } from 'react'
import { fetchAdoptionStories, INITIAL_ADOPTION_STORIES } from '../../services/api'

const AdoptionStories = () => {
  const [stories, setStories] = useState(INITIAL_ADOPTION_STORIES)
  const [startIndex, setStartIndex] = useState(0)
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const isMobile = windowWidth < 768

  // Mouse Drag / Touch Swipe State
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)

  useEffect(() => {
    fetchAdoptionStories().then((data) => {
      if (data && data.length > 0) {
        setStories(data)
      }
    })
  }, [])

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isSm = windowWidth >= 640 && windowWidth < 768
  const isMd = windowWidth >= 768 && windowWidth < 1024
  const isDesktop = windowWidth >= 1024

  const visibleCards = isMobile ? 1 : isSm ? 2 : isMd ? 3 : 4
  const maxStartIndex = Math.max(0, stories.length - visibleCards)

  const handleNext = () => {
    setStartIndex((prev) => (prev >= maxStartIndex ? 0 : prev + 1))
  }

  const handlePrev = () => {
    setStartIndex((prev) => (prev <= 0 ? maxStartIndex : prev - 1))
  }

  // Mouse Drag & Touch Handlers
  const handleDragStart = (clientX) => {
    setIsDragging(true)
    setStartX(clientX)
    setDragOffset(0)
  }

  const handleDragMove = (clientX) => {
    if (!isDragging) return
    const diff = clientX - startX
    setDragOffset(diff)
  }

  const handleDragEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    const threshold = 50 // px threshold to trigger slide shift
    if (dragOffset < -threshold) {
      handleNext()
    } else if (dragOffset > threshold) {
      handlePrev()
    }
    setDragOffset(0)
  }

  const [selectedStory, setSelectedStory] = useState(null)
  const [isModalMounted, setIsModalMounted] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenStory = (story) => {
    setSelectedStory(story)
    setIsModalMounted(true)
    setTimeout(() => setIsModalOpen(true), 20)
  }

  const handleCloseStory = () => {
    setIsModalOpen(false)
    setTimeout(() => {
      setIsModalMounted(false)
      setSelectedStory(null)
    }, 320)
  }

  return (
    <section className="bg-transparent py-8 sm:py-12 lg:py-16 2xl:py-20 select-none relative">
      <div className="max-w-8xl mx-auto w-full">
        
        {/* Header with Navigation Controls */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-4 sm:mb-6 md:mb-8 gap-3 sm:gap-4 px-4 sm:px-6 lg:px-8 2xl:px-16">
          <div className="text-left">
            <span className="text-[11px] sm:text-xs 2xl:text-sm font-bold tracking-widest text-brand-orange-700 uppercase block mb-1">
              FOREVER HOMES
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl text-slate-900 font-semibold tracking-tight">
              Happy Adoption Stories
            </h2>
            <p className="text-slate-600 text-xs sm:text-sm 2xl:text-base font-medium mt-1 sm:mt-1.5 max-w-xl">
              Read how rescued animals found loving forever homes across Bengaluru. Click any story to view full details.
            </p>
          </div>

          {/* Desktop Navigation Controls (Top Right) */}
          {stories.length > visibleCards && (
            <div className="hidden md:flex items-center gap-2.5 self-start sm:self-auto">
              <button
                onClick={handlePrev}
                className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center bg-brand-orange-700 hover:bg-brand-orange-850 text-white transition-all shadow-md focus:outline-none cursor-pointer border border-brand-orange-800 active:scale-95"
                aria-label="Previous adoption story"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>
              <button
                onClick={handleNext}
                className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center bg-brand-orange-700 hover:bg-brand-orange-850 text-white transition-all shadow-md focus:outline-none cursor-pointer border border-brand-orange-800 active:scale-95"
                aria-label="Next adoption story"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Sliding & Drag-enabled Carousel track */}
        <div 
          className="w-full overflow-hidden py-1 sm:py-2 px-4 sm:px-6 lg:px-8 2xl:px-16 cursor-grab active:cursor-grabbing touch-pan-y select-none"
          onMouseDown={(e) => handleDragStart(e.clientX)}
          onMouseMove={(e) => handleDragMove(e.clientX)}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
          onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
          onTouchEnd={handleDragEnd}
        >
          <div
            className={`flex gap-3.5 sm:gap-5 lg:gap-5 ${isDragging ? 'transition-none' : 'transition-transform duration-500 ease-in-out'}`}
            style={{
              transform: `translateX(calc(-${startIndex} * (${
                isMobile ? '84%' : isSm ? '50%' : isMd ? '33.333%' : '25%'
              } + ${isMobile ? '0.875rem' : '1.25rem'}) + ${dragOffset}px))`
            }}
          >
            {stories.map((story) => (
              <div
                key={story.id}
                onClick={() => handleOpenStory(story)}
                className="w-[84%] sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-0.85rem)] lg:w-[calc(25%-0.95rem)] 2xl:w-[calc(25%-1rem)] shrink-0 bg-white rounded-tl-[32px] rounded-br-[32px] sm:rounded-tl-[44px] sm:rounded-br-[44px] 2xl:rounded-tl-[52px] 2xl:rounded-br-[52px] rounded-tr-none rounded-bl-none p-3.5 sm:p-4 lg:p-4.5 transition-all duration-300 flex flex-col justify-between hover:-translate-y-1.5 cursor-pointer group text-left shadow-xs hover:shadow-xl border border-slate-100/90 relative"
              >
                {/* Pet Photo - Curved image frame */}
                <div className="relative h-38 xs:h-42 sm:h-46 md:h-48 lg:h-48 2xl:h-54 w-full rounded-tl-[24px] rounded-br-[24px] sm:rounded-tl-[32px] sm:rounded-br-[32px] 2xl:rounded-tl-[40px] 2xl:rounded-br-[40px] rounded-tr-none rounded-bl-none overflow-hidden bg-brand-orange-50/50 mb-3 shrink-0">
                  <img
                    src={story.image_url || story.image || '/adopters/1.png'}
                    alt={story.pet_name || 'Adopted animal story'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/adopters/1.png'; }}
                  />
                  {story.date && (
                    <span className="absolute bottom-2 left-2 sm:bottom-2.5 sm:left-2.5 px-2 py-0.5 sm:px-2.5 sm:py-0.5 bg-white/95 text-slate-800 text-[9px] xs:text-[10px] font-extrabold uppercase tracking-widest rounded-md shadow-xs border border-white/60">
                      {story.date}
                    </span>
                  )}
                </div>

                {/* Card Description & Meta */}
                <div className="flex flex-col flex-grow justify-between px-0.5 space-y-2">
                  <p className="text-slate-700 text-xs sm:text-xs md:text-sm font-semibold leading-snug sm:leading-relaxed line-clamp-3">
                    "{story.quote}"
                  </p>
                  
                  {(story.pet_name || story.adopter_name) && (
                    <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between">
                      <h3 className="font-bold text-slate-900 text-[11px] sm:text-xs md:text-sm">
                        {story.pet_name && <span className="text-brand-orange-700">{story.pet_name}</span>}
                        {story.pet_name && story.adopter_name && <span className="text-slate-400 font-normal"> with </span>}
                        {story.adopter_name && <span className="text-slate-700">{story.adopter_name}</span>}
                      </h3>
                      <span className="text-[10px] font-extrabold text-brand-orange-600 group-hover:translate-x-0.5 transition-transform">
                        Read &rarr;
                      </span>
                    </div>


                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Navigation Controls (Bottom Right - Separate Dark Maroon Circular Buttons) */}
        {stories.length > visibleCards && (
          <div className="flex md:hidden justify-end items-center gap-2.5 mt-3 sm:mt-4 px-4 sm:px-6 w-full">
            <button
              onClick={handlePrev}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-orange-700 active:bg-brand-orange-850 text-white transition-all shadow-md focus:outline-none cursor-pointer border border-brand-orange-800 active:scale-95"
              aria-label="Previous story"
            >
              <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-orange-700 active:bg-brand-orange-850 text-white transition-all shadow-md focus:outline-none cursor-pointer border border-brand-orange-800 active:scale-95"
              aria-label="Next story"
            >
              <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </button>
          </div>
        )}

      </div>

      {/* Adoption Story Slide-Up Pop-up Drawer */}
      {(isModalMounted || isModalOpen) && selectedStory && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity duration-300 z-50 ${
              isModalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={handleCloseStory}
          />

          {/* Standard Slide-Over Drawer Container (Right-docked matching form drawers) */}
          <div
            data-lenis-prevent
            className={`fixed top-0 right-0 h-full w-full sm:w-[480px] lg:w-[520px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out transform border-l border-slate-200 ${
              isModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >

            {/* Header with Close X */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-brand-orange-700 block">
                  HAPPY ADOPTION STORY
                </span>
                <h3 className="text-xl sm:text-2xl font-bold font-display text-slate-900 leading-tight">
                  {selectedStory.pet_name || 'Rescue Transformation'}
                </h3>
              </div>

              <button
                onClick={handleCloseStory}
                className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-full transition-all focus:outline-none cursor-pointer shrink-0"
                aria-label="Close story details"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable Story Content */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4 text-left">
              {/* Full Image */}
              <div className="relative w-full h-56 sm:h-64 rounded-2xl overflow-hidden bg-slate-100 border border-slate-100 shadow-sm">
                <img
                  src={selectedStory.image_url || selectedStory.image || '/adopters/1.png'}
                  alt={selectedStory.pet_name || 'Adopted pet photo'}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.onerror = null; e.target.src = '/adopters/1.png'; }}
                />
                {selectedStory.date && (
                  <span className="absolute bottom-3 left-3 px-3 py-1 bg-white/95 text-slate-800 text-xs font-black uppercase tracking-wider rounded-lg shadow-sm border border-white/80">
                    {selectedStory.date}
                  </span>
                )}
              </div>

              {/* Adopter & Pet Meta */}
              <div className="pb-2 border-b border-slate-100">
                <h4 className="text-lg font-bold text-slate-900">
                  {selectedStory.pet_name && <span className="text-brand-orange-700">{selectedStory.pet_name}</span>}
                  {selectedStory.pet_name && selectedStory.adopter_name && <span className="text-slate-400 font-normal"> with </span>}
                  {selectedStory.adopter_name && <span className="text-slate-800">{selectedStory.adopter_name}</span>}
                </h4>
              </div>

              {/* Transformation Quote / Full Story */}
              <div className="space-y-2">
                <span className="text-[10px] uppercase font-extrabold tracking-widest text-slate-400 block">Transformation Journey</span>
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed font-medium bg-orange-50/50 p-4 rounded-2xl border border-orange-100/80 italic">
                  "{selectedStory.quote}"
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

export default AdoptionStories

